import React, { useMemo, useState } from 'react'
import { Alert } from '@mui/material'
import FormDialog from '../../../components/dialogs/FormDialog'
import { resetPassword } from '../../services/apiUserService'
import TemporaryAccessSuccessModal from './TemporaryAccessSuccessModal'
import {
  formatTemporaryAccessRemaining,
  markTemporaryAccessIssued,
  resolveInitialResetDialogKind,
  TEMPORARY_ACCESS_TTL_MS,
  getTemporaryAccessExpiresAt
} from '../../helpers/helpers'
import { UI_TEXT_CANCEL } from '../../../constants/uiText'
import {
  getResetDialogContent,
  TEMPORARY_ACCESS_TEXT
} from '../../constants/temporaryAccessConstants'
import useTemporaryAccessCountdown from '../../hooks/hooks'
import TemporaryAccessParagraphs from './TemporaryAccessParagraphs'
import '../../../css/temporary_access.scss'

const ResetPasswordModal = ({ user, closeMainModal }) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [access, setAccess] = useState(null)
  const [phase, setPhase] = useState('dialog')
  const dialogKind = useMemo(() => resolveInitialResetDialogKind(user.uid), [user.uid])
  const [isReplacement, setIsReplacement] = useState(false)
  const expiresAt = useMemo(() => getTemporaryAccessExpiresAt(user.uid), [user.uid])
  const remainingMs = useTemporaryAccessCountdown(dialogKind === 'replace' ? expiresAt : null)
  const dialogContent = getResetDialogContent(dialogKind)

  const issueAccess = async () => {
    if (busy) return
    const replacing = dialogKind === 'replace' || dialogKind === 'expired'
    setBusy(true)
    setError(null)
    try {
      const response = await resetPassword(user)
      const data = await response.json()
      if (!response.ok || !data.temporary_password) {
        throw new Error(data.error || data.message || TEMPORARY_ACCESS_TEXT.generationError)
      }
      markTemporaryAccessIssued(user.uid)
      const expiresAt = Date.now() + TEMPORARY_ACCESS_TTL_MS
      setIsReplacement(replacing)
      setAccess({ ...data, email: user.email, expiresAt })
      setPhase('success')
    } catch (failure) {
      setError(failure.message)
    } finally {
      setBusy(false)
    }
  }

  if (phase === 'success') {
    return (
      <TemporaryAccessSuccessModal
        action={isReplacement ? 'reset-replacement' : 'reset'}
        access={access}
        onAccessClose={() => setAccess(null)}
        onComplete={closeMainModal}
      />
    )
  }

  return (
    <FormDialog
      open
      title={dialogContent.title}
      subtitle={dialogContent.subtitle}
      maxWidth="md"
      paperClassName="temporary-access-paper temporary-access-reset"
      loading={busy}
      onClose={closeMainModal}
      onSubmit={issueAccess}
      submitLabel={busy ? TEMPORARY_ACCESS_TEXT.generating : dialogContent.submitLabel}
      cancelLabel={UI_TEXT_CANCEL}
    >
      <TemporaryAccessParagraphs
        paragraphs={dialogContent.paragraphs}
        values={{
          email: user.email,
          remaining: formatTemporaryAccessRemaining(remainingMs),
          validity: TEMPORARY_ACCESS_TEXT.validity
        }}
      />
      {error && (
        <Alert severity="error" className="temporary-access-error">
          {error}
        </Alert>
      )}
    </FormDialog>
  )
}
export default ResetPasswordModal
